import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import {
  aiDispositionAdd,
  aiDispositionUpdate
} from '../../../store/slices/tenantSlices/settingSlice';
import { callexAiAgentOptionsGet } from '../../../store/slices/tenantSlices/configurationSlice';

const categoryOptions = [
  { label: 'Sales', value: 'Sales' },
  { label: 'Support', value: 'Support' },
  { label: 'General', value: 'General' }
];

const AddEditAIDispositionModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const [aiPhoneNumberOption, setAiPhoneNumberOption] = useState([]);

  const initialValues = {
    name: '',
    tagline: '',
    category: '',
    requiresNote: false,
    active: true,
    linkedAgents: [],
    requiredFields: [{ name: '', type: '' }]
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    tagline: Yup.string().required('Tagline is required'),
    category: Yup.string().required('Category is required'),
    linkedAgents: Yup.array().min(1, 'At least one agent is required').required('Required')
  });

  const onSubmit = async (values, { resetForm }) => {
    try {
      if (isEdit) {
        const payload = { ...values, id: editRecord.id };
        const response = await dispatch(aiDispositionUpdate(payload)).unwrap();
        if (response && response.success) {
          toast.success(response.message || 'Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        }
      } else {
        const response = await dispatch(aiDispositionAdd(values)).unwrap();
        if (response && response.success) {
          toast.success(response.message || 'Record Added Successfully');
          onCloseClick(true);
          resetForm();
        }
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getAIAgentOption = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data?.agents.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAiPhoneNumberOption(options);
      } else {
        setAiPhoneNumberOption([]);
      }
    } catch (error) {
      setAiPhoneNumberOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getAIAgentOption();
  }, []);

  const fieldTypeOptions = [
    { label: 'String', value: 'string' },
    { label: 'Number', value: 'number' },
    { label: 'Boolean', value: 'boolean' }
  ];

  useEffect(() => {
    if (isEdit && editRecord) {
      setValues({
        name: editRecord.name || '',
        tagline: editRecord.tagline || '',
        category: editRecord.category || '',
        requiresNote: !!editRecord.requiresNote,
        active: editRecord.active !== undefined ? !!editRecord.active : true,
        linkedAgents: editRecord.linkedAgents || [],
        requiredFields:
          editRecord.requiredFields && editRecord.requiredFields.length > 0
            ? editRecord.requiredFields.map((f) => ({
                name: f.name || '',
                type: f.type || ''
              }))
            : [{ name: '', type: '' }]
      });
    } else {
      resetForm();
    }
  }, [isEdit, editRecord, isOpen]);

  const addRequiredField = () => {
    setFieldValue('requiredFields', [...values.requiredFields, { name: '', type: '' }]);
  };

  const removeRequiredField = (index) => {
    const updated = values.requiredFields.filter((_, i) => i !== index);
    setFieldValue('requiredFields', updated);
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit AI Disposition' : 'Add AI Disposition'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="ai-disposition-form" onSubmit={handleSubmit}>
          <Row className="mt-3">
            <Col lg="4">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="4">
              <FormSelect
                name="category"
                label="Category"
                placeholder="Select Category"
                value={values.category}
                options={categoryOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="4">
              <FormSelect
                name="linkedAgents"
                label="Linked Agents"
                placeholder="Select Agents"
                value={values.linkedAgents}
                options={aiPhoneNumberOption}
                isMulti
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col lg="6">
              <FormInput
                name="tagline"
                label="Tagline"
                placeholder="Enter Tagline"
                value={values.tagline}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="requiresNote"
                label="Requires Note"
                value={String(values.requiresNote)}
                onChange={(e) => setFieldValue('requiresNote', e === 'true')}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="active"
                label="Active"
                value={String(values.active)}
                onChange={(e) => setFieldValue('active', e === 'true')}
              />
            </Col>
          </Row>

          <div className="w-100 py-2 px-3 mb-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
            <div>Fields</div>
          </div>
          {values.requiredFields.map((field, index) => (
            <Row key={index} className="align-items-end mb-3 g-2">
              <Col lg="6">
                <FormInput
                  label="Field Name"
                  name={`requiredFields[${index}].name`}
                  placeholder="Enter field name"
                  value={field.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  className="table-select"
                />
              </Col>
              <Col lg="4">
                <FormSelect
                  label="Data Type"
                  name={`requiredFields[${index}].type`}
                  placeholder="Select type"
                  value={field.type}
                  options={fieldTypeOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  className="table-select"
                />
              </Col>
              <Col lg="2" className="d-flex align-items-center gap-1 justify-content-end">
                <div
                  className="icon-action danger cursor-pointer"
                  onClick={() => removeRequiredField(index)}>
                  <Icon.Trash size={14} />
                </div>
                {index === values.requiredFields.length - 1 && (
                  <div className="icon-action info cursor-pointer" onClick={addRequiredField}>
                    <Icon.Plus size={14} />
                  </div>
                )}
              </Col>
            </Row>
          ))}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="ai-disposition-form" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditAIDispositionModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditAIDispositionModal;
